import { useRef, useState, type JSX } from "react";
import { useReactToPrint } from "react-to-print";
import { Download, Loader2 } from "lucide-react";

import { ReusableModal, type ModalProps } from "../../components/Modal";
import { RiskPlusReportStore } from "../../stores/RiskPlusReportStore";
import { RenderRiskPlusReport } from "./RiskPlusReport.renderer";
import CustomButton from "../../components/Button";
import { PrintInfoModal } from "@/components/PrintInfoModal";
// import DownloadReportButton from "./pdf/DownloadReportButton";


export interface ReportDetailModalProps extends Partial<ModalProps> {
  onClose: () => void,
  isOpen: boolean
}


export const ReportDetailModal = (props: ReportDetailModalProps): JSX.Element => {
  const reportLoading = RiskPlusReportStore.useRiskPlusReportStore((state) => state.isLoading);
  const riskReportData = RiskPlusReportStore.useRiskPlusReportStore((state) => state.riskPlusReport);
  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [infoModal, setInfoModal] = useState(false);

  const documentTitle = riskReportData?.titlePage?.title ?? "Report";

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle,
    onBeforePrint: async () => setIsPrinting(true),
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const handleDownloadReport = () => {
    setInfoModal(true)
  };

  const onCloseInfo = () => {
    setInfoModal(false)
  }

  const onConfirmationInfo = () => {
    setInfoModal(false)
    void handlePrint();
  }

  return (
    <ReusableModal
      {...props}
      size="full"
      closeButtonText="Close"
    >
      <>
        <div className="no-print w-full border-b border-gray-100 pb-3 mb-4 flex items-center gap-3">
          {riskReportData && (
            <>
              {/* <DownloadReportButton
                reportData={riskReportData}
                fileName={`${documentTitle}.pdf`}
              /> */}
              <button
                onClick={handleDownloadReport}
                disabled={isPrinting}
                className="inline-flex items-center gap-2 rounded-lg border border-midnight-navi px-4 py-2 text-sm font-semibold text-midnight-navi shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isPrinting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Print Report
                  </>
                )}
              </button>
            </>
          )}
        </div>

        <div className="h-[95vh] overflow-y-auto">
          {reportLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-gray-500">Loading report...</div>
            </div>
          ) : riskReportData ? (
            <>
              <div className="no-print w-full pt-1 pb-3 flex justify-center">
                <CustomButton
                  type="button"
                  color="secondary"
                  className="!bg-white !border !border-deep-blue !text-deep-blue hover:!bg-deep-blue/5 hover:!border-deep-blue rounded-lg text-sm font-medium shadow-sm px-5 py-2.5 transition-colors"
                  onPress={props.onClose}
                >
                  Close
                </CustomButton>
              </div>
              <RenderRiskPlusReport ref={printRef} reportData={riskReportData} />
              <div className="no-print w-full pt-4 mt-4 pb-2 flex justify-center border-t border-gray-100">
                <CustomButton
                  type="button"
                  color="secondary"
                  className="!bg-white !border !border-deep-blue !text-deep-blue hover:!bg-deep-blue/5 hover:!border-deep-blue rounded-lg text-sm font-medium shadow-sm px-5 py-2.5 transition-colors"
                  onPress={props.onClose}
                >
                  Close
                </CustomButton>
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center h-full">
              <div className="text-gray-500 text-center">
                <p>No report data available</p>
                <p className="text-sm mt-2">Please try again or contact support</p>
                <div className="no-print w-full pt-4 mt-4 flex justify-center">
                  <CustomButton
                    type="button"
                    aria-label="Close view"
                    color="secondary"
                    className="!bg-white !border !border-deep-blue !text-deep-blue hover:!bg-deep-blue/5 hover:!border-deep-blue rounded-lg text-sm font-medium shadow-sm px-5 py-2.5 transition-colors"
                    onPress={props.onClose}
                  >
                    Close
                  </CustomButton>
                </div>
              </div>
            </div>
          )}
        </div>

        <PrintInfoModal onClose={onCloseInfo} onConfirmation={onConfirmationInfo} isOpen={infoModal} />
      </>
    </ReusableModal>
  )
}
